import { FormControl, FormLabel, MenuItem, Select } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

import SelectPlaceholder from '../selectPlaceholder';

const defaultTitle = 'Region';

export const RestoreRegionSelect = ({
  restoreAccountId,
  regionName,
  onChange,
  cloudProvider,
  isRequired = false,
}: {
  restoreAccountId?: string;
  regionName?: string;
  onChange: (regionName?: string, isUserChange?: boolean) => void;
  cloudProvider: Provider;
  isRequired?: boolean;
}) => {
  const dal = useDAL();
  const title = `${defaultTitle}${isRequired ? '*' : ''}`;

  if (!restoreAccountId) {
    return <EmptySelect title={title} />;
  }

  return (
    <InnerRegionSelect
      accountId={restoreAccountId}
      regionName={regionName}
      onChange={onChange}
      getRegionsCallback={
        cloudProvider === Provider.Aws || cloudProvider === Provider.Gcp
          ? () => dal.restore.regions.list(restoreAccountId)
          : cloudProvider === Provider.Azure
            ? () => dal.restore.azureRestoreConfig.listRegions()
            : () => {
                throw new Error('Unsupported cloud provider');
              }
      }
      title={title}
    />
  );
};

const InnerRegionSelect = ({
  getRegionsCallback,
  regionName,
  onChange,
  title,
}: {
  accountId: string;
  regionName?: string;
  onChange: (regionName?: string, isUserChange?: boolean) => void;
  getRegionsCallback:
    | (() => ReturnType<
        ReturnType<typeof useDAL>['restore']['regions']['list']
      >)
    | (() => ReturnType<
        ReturnType<
          typeof useDAL
        >['restore']['azureRestoreConfig']['listRegions']
      >);
  title: string;
}) => {
  const { data: body, isLoading } = getRegionsCallback();

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (regionName && !body?.regions.includes(regionName)) {
      onChange(undefined);
    }
  }, [body?.regions, isLoading, onChange, regionName]);

  if (!body?.regions.length) {
    return <EmptySelect title={title} />;
  }

  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>{title}</FormLabel>
      <Select
        size='small'
        value={regionName || ''}
        onChange={(event) => onChange(event.target.value, !!event)}
        displayEmpty
        renderValue={(value: string) => {
          if (!value) {
            return <SelectPlaceholder />;
          }
          return value;
        }}
      >
        {body.regions.map((region) => (
          <MenuItem key={region} value={region}>
            {region}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
